/**
 * i18n plumbing (web plan W3.0, master D-11): **the URL decides the locale;
 * i18n only translates.**
 *
 * - One catalogue per language (`./locales/en.json`), namespaced per page
 *   (`home`, `brands`, `promotion`, …; `common` for what pages share),
 *   bundled with the app and with the SEO server: no request, no backend.
 * - The language is the route's literal `/{cc}/{lang}` segment (`/pk/en` →
 *   `en`), or DEFAULT_LOCALE's on un-prefixed pages. Nothing here reads the
 *   browser language, `Accept-Language`, a cookie, storage, the session or
 *   `?lng=`: there is no language detector and never a redirect by language.
 *   (W4: `remix-i18next` is configured the same way — the route's `lang`
 *   segment only.)
 * - Pure module (no `window`, no React): the SPA (`./LocaleLanguage`) and the
 *   SEO server (`server/routes.mjs` → `@/seo/templates`) read the same
 *   instances, so both build identical titles.
 * - Dates, numbers and prices are formatted by `./format` (Intl with the
 *   locale's formatting locale, time zone and currency) and passed to `t()` as
 *   values; catalogue strings never format.
 */
import i18next, { type i18n as I18n, type TOptions } from 'i18next';
import { DEFAULT_LOCALE, LOCALES, type Locale } from '@/seo/locales';
import en from './locales/en.json';

/** The bundled catalogues, by language. Every launched locale's language needs one. */
export const CATALOGUES = Object.freeze({ en });

export type Language = keyof typeof CATALOGUES;

/** The namespaces (one per page, plus `common`): the catalogue's top-level keys. */
export const NAMESPACES: readonly string[] = Object.freeze(Object.keys(en));

/** The language of un-prefixed pages, and the fallback for a missing string. */
export const DEFAULT_LANGUAGE = DEFAULT_LOCALE.lang;

/** Values a catalogue string interpolates (`{{name}}`); `count` also picks the plural form. */
export type TranslateValues = Readonly<Record<string, string | number>>;

/**
 * A translation function for one language: `t('brands:head.h1', { country })`.
 * `@/seo/templates` takes one, so the SPA (react-i18next's instance of the
 * route) and the SEO server (the same catalogue in Node) pass theirs.
 */
export type Translate = (key: string, values?: TranslateValues) => string;

/**
 * The formatting locale of a route's locale: its literal language and country
 * segments, `/pk/en` → `en-PK` (BCP 47, what `Intl` and `<html lang>` take).
 */
export function formattingLocale(locale: Locale = DEFAULT_LOCALE): string {
  return `${locale.lang}-${locale.cc.toUpperCase()}`;
}

function createBase(): I18n {
  const instance = i18next.createInstance();
  // Synchronous: the resources are inline, so `t` works as soon as this
  // module has loaded (module constants in @/seo/templates read it).
  void instance.init({
    resources: { ...CATALOGUES },
    lng: DEFAULT_LANGUAGE,
    fallbackLng: DEFAULT_LANGUAGE,
    ns: [...NAMESPACES],
    defaultNS: 'common',
    initAsync: false,
    // Titles and alt text are plain text; React and server/head.mjs escape.
    interpolation: { escapeValue: false },
    returnNull: false,
    react: { useSuspense: false },
  });
  return instance;
}

const base = createBase();
const instances = new Map<string, I18n>([[DEFAULT_LANGUAGE, base]]);

/**
 * The i18next instance for a locale's language (`locale.lang`, the literal URL
 * segment). One instance per language, sharing the bundled resources; the
 * language of an instance never changes, so rendering a page never switches
 * another page's language.
 */
export function i18nFor(locale: Locale = DEFAULT_LOCALE): I18n {
  let instance = instances.get(locale.lang);
  if (!instance) {
    instance = base.cloneInstance({ lng: locale.lang });
    instances.set(locale.lang, instance);
  }
  return instance;
}

/** A held value's stand-in: NUL never occurs in a catalogue string. */
const HELD = /\u0000(\d+)\u0000/g;

/**
 * A `Translate` over an i18next instance (react-i18next's, or `i18nFor`'s).
 *
 * Values are data (brand, promotion and city names typed by staff), so they
 * must come out exactly as written. i18next's own interpolation does not
 * guarantee that: it replaces a placeholder by searching the string, so a
 * value containing `{{country}}` gets the country filled in when the
 * template has a `{{country}}` after it. Each value is therefore handed to
 * i18next as a stand-in and put back in one pass afterwards, where nothing
 * inserted is read again. `count` stays a number (it picks the plural form).
 * Consequence: catalogue strings never use i18next's `{{value, format}}`
 * (format with `./format` first) and never nest a value into `$t(…)`.
 */
export function translatorOf(instance: I18n): Translate {
  return (key, values) => {
    if (!values) return String(instance.t(key));
    const held: string[] = [];
    const stand: Record<string, string | number> = {};
    for (const [name, value] of Object.entries(values)) {
      stand[name] =
        name === 'count' && typeof value === 'number' ? value : `\u0000${held.push(String(value)) - 1}\u0000`;
    }
    return String(instance.t(key, stand as TOptions)).replace(HELD, (whole, index: string) => held[Number(index)] ?? whole);
  };
}

const translators = new Map<string, Translate>();

/** The `Translate` for a locale's language: what the SEO server and the templates' defaults use. */
export function tFor(locale: Locale = DEFAULT_LOCALE): Translate {
  let t = translators.get(locale.lang);
  if (!t) {
    t = translatorOf(i18nFor(locale));
    translators.set(locale.lang, t);
  }
  return t;
}

/** Every launched locale's language has a catalogue (checked by src/i18n/i18n.test.ts). */
export function missingCatalogues(locales: readonly Locale[] = LOCALES): string[] {
  return locales.map((locale) => locale.lang).filter((lang) => !(lang in CATALOGUES));
}
